import { getCore } from 'cvat-core-wrapper';

export interface ReferencePhoto {
    referenceId: number;
    referenceName: string;
    jobId: number;
    frame: number;
    // position in the references strip, 0-based
    index: number;
    name?: string;
}

export interface FindModelsResult {
    shapes: { id: number; value: string | null; candidates: number }[];
}

interface Page<T> { results: T[] }

async function get<T>(path: string, params: Record<string, unknown>): Promise<T> {
    const core = getCore();
    const response = await core.server.request(`${core.config.backendAPI}${path}`, { method: 'GET', params });
    return response.data as T;
}

export async function fetchTaskReferencePhotos(taskId: number): Promise<ReferencePhoto[]> {
    const filter = JSON.stringify({ and: [{ '==': [{ var: 'task_id' }, taskId] }] });
    let references: { id: number; name: string }[];
    try {
        references = (await get<Page<{ id: number; name: string }>>('/references', { filter, page_size: 100 })).results;
    } catch {
        // reference rights are per reference, not per task: unreadable means none to show
        return [];
    }
    const photos: ReferencePhoto[] = [];
    for (const reference of [...references].sort((a, b) => a.id - b.id)) {
        let jobs: { id: number; start_frame: number; stop_frame: number; type: string }[] = [];
        try {
            jobs = (await get<Page<typeof jobs[number]>>('/reference_jobs', {
                reference_id: reference.id, type: 'annotation', page_size: 100,
            })).results;
        } catch {
            jobs = [];
        }
        for (const job of jobs.filter((item) => item.type === 'annotation').sort((a, b) => a.id - b.id)) {
            let deleted: number[] = [];
            let frames: { name?: string }[] = [];
            try {
                const meta = await get<{ deleted_frames?: number[]; frames?: { name?: string }[] }>(
                    `/reference_jobs/${job.id}/data/meta`, {},
                );
                deleted = meta.deleted_frames ?? [];
                frames = meta.frames ?? [];
            } catch {
                deleted = [];
            }
            for (let frame = job.start_frame; frame <= job.stop_frame; frame++) {
                if (deleted.includes(frame)) continue;
                photos.push({
                    referenceId: reference.id,
                    referenceName: reference.name,
                    jobId: job.id,
                    frame,
                    index: photos.length,
                    name: frames[frame - job.start_frame]?.name,
                });
            }
        }
    }
    return photos;
}

export async function fetchTaskJobIds(taskId: number): Promise<number[]> {
    const page = await get<Page<{ id: number; type: string }>>('/jobs', { task_id: taskId, type: 'annotation', page_size: 100 });
    return page.results.filter((job) => job.type === 'annotation').map((job) => job.id).sort((a, b) => a - b);
}

export async function findModels(jobId: number, taskId: number, shapeIds: number[]): Promise<FindModelsResult> {
    const core = getCore();
    const response = await core.server.request(`${core.config.backendAPI}/reference_jobs/${jobId}/find_models`, {
        method: 'POST', data: { shapes: shapeIds, task_id: taskId },
    });
    return response.data as FindModelsResult;
}

export async function findDrawingModels(jobId: number, shapeIds: number[]): Promise<void> {
    const core = getCore();
    await core.server.request(`${core.config.backendAPI}/jobs/${jobId}/find_models`, {
        method: 'POST', data: { shapes: shapeIds },
    });
}

// attribute value -> UNC path of its .blend; null when the server cannot place it
export async function fetchWindowsPaths(values: string[]): Promise<Record<string, string | null>> {
    const core = getCore();
    const response = await core.server.request(`${core.config.backendAPI}/library_paths`, {
        method: 'POST', data: { values },
    });
    return (response.data as { paths: Record<string, string | null> }).paths;
}
